/**
 * Uninventable property registry.
 *
 * A property is UNINVENTABLE when it asserts a fact about the world that the
 * page does not state, and a model filling it in is therefore publishing a
 * falsehood under the customer's domain. A star rating and a price are the
 * clearest cases: both are load-bearing for a buyer, both are trivially
 * plausible to invent, and neither can be recovered from prose.
 *
 * This generalizes VIDEO_UNINVENTABLE_PROPERTIES, which protected exactly two
 * VideoObject fields from inside the refinement planner. Every type added from
 * here on registers its own rules in one place, so a new type cannot quietly
 * ship without anyone deciding which of its fields are safe to generate.
 *
 * THE CONTRACT
 *
 * A rule survives only when its evidence token is present in the token set the
 * caller assembled FROM THE PAGE. Tokens must never be derived from the
 * business profile, account settings, or a previous generation: those are not
 * evidence that this page says the thing, and treating them as evidence is the
 * exact hole this module exists to close.
 *
 * Removing a property is always the safe outcome. A schema missing an optional
 * field is valid and merely less decorated. A schema carrying an invented one
 * is a lie with our name on it.
 *
 * A LIMIT OF THIS DESIGN
 *
 * These are property-level rules. They cannot say "drop the whole node",
 * which is what a type whose REQUIRED properties are uninventable actually
 * needs. Review is that type: reviewRating, itemReviewed and author are all
 * required (see schemaScoring.ts), so stripping the rating leaves a
 * structurally invalid Review rather than an honest one. The rule below is
 * damage control, not a fix. The real fix is either a node-level rule here or
 * a Review evidence gate that reads extracted review data instead of a CSS
 * class sweep.
 *
 * INPUT CONTRACT
 *
 * stripUninventableProperties is safe on JSON-derived data only: plain
 * objects, arrays, and primitives, exactly what JSON.parse produces. It
 * rebuilds every node through Object.entries, so a class instance such as
 * Date flattens to `{}` rather than being preserved, and a cyclic graph
 * recurses forever and throws a RangeError rather than degrading gracefully.
 * Neither case is reachable from parsed model output today, but a future
 * caller handing this richer objects needs to know it will not round-trip
 * them.
 */
import { canonicalSchemaTypeKey } from './schemaTypes.js';
export const UNINVENTABLE_RULES = [
    // Preserved verbatim from VIDEO_UNINVENTABLE_PROPERTIES.
    { type: 'VideoObject', property: 'thumbnailUrl', requires: 'video.thumbnailUrl' },
    { type: 'VideoObject', property: 'uploadDate', requires: 'video.uploadDate' },
    // Google requires offers.price AND a rating for the app rich result. Both are
    // uninventable, so most pages will produce a valid but non-rich-eligible
    // SoftwareApplication. That is the correct outcome, not a gap to paper over.
    { type: 'SoftwareApplication', property: 'aggregateRating', requires: 'rating' },
    { type: 'SoftwareApplication', property: 'review', requires: 'rating' },
    { type: 'SoftwareApplication', property: 'offers', requires: 'price' },
    { type: 'WebApplication', property: 'aggregateRating', requires: 'rating' },
    { type: 'WebApplication', property: 'review', requires: 'rating' },
    { type: 'WebApplication', property: 'offers', requires: 'price' },
    // A profile's creation date and follower counts are never on a marketing page.
    { type: 'ProfilePage', property: 'dateCreated', requires: 'profile.dates' },
    { type: 'ProfilePage', property: 'dateModified', requires: 'profile.dates' },
    { type: 'ProfilePage', property: 'interactionStatistic', requires: 'profile.interactionStats' },
    { type: 'ProfilePage', property: 'agentInteractionStatistic', requires: 'profile.interactionStats' },
    // A count is a claim about how many pages exist. It may only survive when the
    // extractor actually produced the list it counts, otherwise a model can widen
    // a list of 8 into a confident claim of 40.
    { type: 'ItemList', property: 'numberOfItems', requires: 'listing.items' },
    // JobPosting. Google's own docs say "Only employers can provide baseSalary".
    // We are not the employer, nothing extracts a salary, and a wrong salary is
    // the most consequential number on the page for the person reading it. The
    // 'price' token is never emitted, so this is always stripped.
    { type: 'JobPosting', property: 'baseSalary', requires: 'price' },
    // A claim about the future. No extractor produces it, and 'job.validThrough'
    // is never emitted, so it is always stripped until one does.
    { type: 'JobPosting', property: 'validThrough', requires: 'job.validThrough' },
    // Emitted only from a <time datetime> on the page. See machineReadableDate.ts.
    { type: 'JobPosting', property: 'datePosted', requires: 'job.datePosted' },
    // Event. startDate is emitted from a real machine-readable date; endDate is a
    // SECOND date that nothing extracts, so it gets its own never-emitted token
    // rather than riding along on startDate's.
    { type: 'Event', property: 'startDate', requires: 'event.startDate' },
    { type: 'Event', property: 'endDate', requires: 'event.endDate' },
    { type: 'Event', property: 'offers', requires: 'price' },
    // Ratings are uninventable wherever they appear. `review` is guarded
    // alongside `aggregateRating` on every one of these types: both carry star
    // data (Google reads review[].reviewRating the same way it reads
    // aggregateRating), so guarding one and not the other lets the identical
    // invented stars through next door.
    { type: 'Product', property: 'aggregateRating', requires: 'rating' },
    { type: 'Product', property: 'review', requires: 'rating' },
    { type: 'LocalBusiness', property: 'aggregateRating', requires: 'rating' },
    { type: 'LocalBusiness', property: 'review', requires: 'rating' },
    { type: 'Organization', property: 'aggregateRating', requires: 'rating' },
    { type: 'Organization', property: 'review', requires: 'rating' },
    { type: 'Service', property: 'aggregateRating', requires: 'rating' },
    { type: 'Service', property: 'review', requires: 'rating' },
    // The Product.offers deferral is over. It read "NOT guarded, deliberately and
    // temporarily", because no extractor emitted a `price` token and a rule here
    // would have stripped every Product price in the fleet with no way to earn it
    // back. Price grounding is that way back: a price now earns the token by
    // appearing on the page (server/src/services/priceGrounding.ts), so these
    // three types can be held to the same standard as the rest.
    //
    // Service and Course are here for the first time. Nobody ever decided they
    // should be unguarded; they were simply never written down, and Service was
    // the widest-spread offers type in the fleet at 19 domains.
    { type: 'Product', property: 'offers', requires: 'price' },
    { type: 'Service', property: 'offers', requires: 'price' },
    { type: 'Course', property: 'offers', requires: 'price' },
    // Damage control, not a fix: see "A LIMIT OF THIS DESIGN" above. This
    // catches a Review's own reviewRating but cannot drop the surrounding
    // Review node, which becomes structurally invalid once its required
    // rating is gone.
    { type: 'Review', property: 'reviewRating', requires: 'rating' },
];
// LocalBusiness subtypes that reach output under their own, more specific
// @type and must inherit every LocalBusiness rule rather than sail past it.
// Mirrors COMMERCIAL_TYPES in
// server/src/services/driftDetectors/commercialDrift.ts (minus Product and
// Service, which already have their own rule entries above). Keep the two
// lists in sync.
const LOCAL_BUSINESS_SUBTYPES = [
    'Restaurant',
    'Hotel',
    'Store',
    'FinancialService',
    'RealEstateAgent',
    'MedicalBusiness',
    'LegalService',
    'ProfessionalService',
    'FoodEstablishment',
    'AutoDealer',
    'AutoRepair',
];
const SCHEMA_ORG_URL_PREFIX = /^https?:\/\/schema\.org\//;
// canonicalSchemaTypeKey only strips the schema.org URL prefix for types it
// already recognizes as SUPPORTED_SCHEMA_TYPES. An unsupported type in URL
// form (e.g. https://schema.org/Restaurant) would otherwise key on the whole
// URL string instead of the type name, so the prefix is stripped here first,
// unconditionally, before canonicalizing.
function typeKey(type) {
    return canonicalSchemaTypeKey(type.replace(SCHEMA_ORG_URL_PREFIX, ''));
}
const RULES_BY_TYPE = new Map();
for (const rule of UNINVENTABLE_RULES) {
    const key = typeKey(rule.type);
    const existing = RULES_BY_TYPE.get(key);
    if (existing)
        existing.push(rule);
    else
        RULES_BY_TYPE.set(key, [rule]);
}
const localBusinessRules = RULES_BY_TYPE.get(typeKey('LocalBusiness')) ?? [];
for (const subtype of LOCAL_BUSINESS_SUBTYPES) {
    RULES_BY_TYPE.set(typeKey(subtype), localBusinessRules);
}
export function uninventablePropertiesForType(type) {
    return (RULES_BY_TYPE.get(typeKey(type)) ?? []).map((rule) => rule.property);
}
function typesOf(node) {
    const raw = node['@type'];
    if (typeof raw === 'string')
        return [raw];
    if (Array.isArray(raw))
        return raw.filter((t) => typeof t === 'string');
    return [];
}
function rulesFor(node) {
    const out = [];
    for (const type of typesOf(node)) {
        const rules = RULES_BY_TYPE.get(typeKey(type));
        if (rules)
            out.push(...rules);
    }
    return out;
}
function walk(value, path, tokens, removed) {
    if (Array.isArray(value)) {
        return value.map((item, index) => walk(item, `${path}[${index}]`, tokens, removed));
    }
    if (!value || typeof value !== 'object')
        return value;
    const node = value;
    const applicable = rulesFor(node);
    // A property already holding `null` was never a fabrication, it is just
    // empty. Drop it silently so the schema stays clean, but do not log it in
    // `removed`: that log means "we caught invented content," not "we tidied
    // up an empty field."
    const nullDoomed = new Set();
    const doomed = new Map();
    for (const rule of applicable) {
        const current = node[rule.property];
        if (current === undefined)
            continue;
        if (current === null) {
            nullDoomed.add(rule.property);
            continue;
        }
        if (!tokens.has(rule.requires)) {
            doomed.set(rule.property, rule);
        }
    }
    const out = {};
    for (const [key, child] of Object.entries(node)) {
        // JSON.parse creates "__proto__" as an ordinary own key, but assigning it
        // back via `out[key] = ...` on a plain object literal does not create a
        // data property: it repoints out's prototype instead. Drop the key
        // rather than risk prototype pollution; no legitimate schema.org
        // property is named this.
        if (key === '__proto__')
            continue;
        if (nullDoomed.has(key))
            continue;
        const rule = doomed.get(key);
        if (rule) {
            removed.push({ path: `${path}.${rule.property}`, type: rule.type, property: rule.property, requires: rule.requires });
            continue;
        }
        out[key] = walk(child, `${path}.${key}`, tokens, removed);
    }
    return out;
}
/**
 * Remove every property whose evidence token is absent. Returns new objects;
 * the input is never mutated.
 */
export function stripUninventableProperties(schemas, tokens) {
    const removed = [];
    const out = schemas.map((schema, index) => walk(schema, `schemas[${index}]`, tokens, removed));
    return { schemas: out, removed };
}
